// ImportFromAssetsDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { FolderOpen, ExternalLink, FileText } from 'lucide-react';

/**
 * Dialog for importing assets from Asset Library to AI Resource Library
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Array} props.linkedAssets - List of available assets from Asset Library
 * @param {function} props.onImportAsset - Callback when an asset is selected for import (receives asset id)
 * @param {function} props.onCancel - Callback when cancel is clicked
 * @param {function} props.onOpenAssetRepository - Callback to navigate to asset repository
 */
export const ImportFromAssetsDialog = ({
  open,
  onOpenChange,
  linkedAssets = [],
  onImportAsset,
  onCancel,
  onOpenAssetRepository
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-blue-600" />
            Import from Asset Library
          </DialogTitle>
          <DialogDescription>
            Select assets from your Asset Library to import into the AI Resource Library.
            These will be available for AI agents to use when generating content.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 overflow-y-auto max-h-[50vh]">
          {linkedAssets.length === 0 ? (
            <div className="text-center py-8">
              <FolderOpen className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No assets available in Asset Library</p>
              <Button 
                variant="outline" 
                className="mt-3" 
                onClick={onOpenAssetRepository}
                data-testid="go-to-assets-empty"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Go to Asset Repository
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {linkedAssets.map((asset) => (
                <Card 
                  key={asset.id} 
                  className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => onImportAsset(asset.id)}
                  data-testid={`import-asset-${asset.id}`}
                >
                  <div className="aspect-video bg-slate-100 relative">
                    {asset.file_url && asset.asset_type === 'image' ? (
                      <img 
                        src={asset.file_url}
                        alt={asset.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="h-8 w-8 text-slate-400" />
                      </div>
                    )}
                    <Badge className="absolute top-2 right-2 bg-blue-600 text-xs">
                      {asset.asset_type}
                    </Badge>
                  </div>
                  <CardContent className="p-2">
                    <p className="font-medium text-sm truncate">{asset.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {asset.description || 'Click to import'}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="import-assets-cancel">
            Cancel
          </Button>
          <Button variant="outline" onClick={onOpenAssetRepository} data-testid="open-asset-repo">
            <ExternalLink className="h-4 w-4 mr-2" />
            Open Asset Repository
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFromAssetsDialog;
